import { FC, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z, ZodType } from 'zod';
import { Grid, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import StructuredModal from '../../../../components/structuredModal/structuredModal';
import ControlledTextField from '../../../../components/controlledComponents/ControlledTextField.component';
import ControlledSelectField from '../../../../components/controlledComponents/ControlledSelectField.component';
import ControlledDateField from '../../../../components/controlledComponents/ControlledDateField.component';
import {
  IAcademicsLibraryItem,
  LibraryAssetType,
  BorrowingStatus,
} from '../../../../../apis/academics/academicsLibrary.interfaces';
import { MaterialCondition } from '../../../../../apis/academics/academicsMaterials.interfaces';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { TKeyValue } from 'src/utils/commonInterfaces';
import { localStorageGet } from 'src/utils/localStorage';
import { ILoggedInUser } from 'src/apis/auth/auth.interfaces';
import {
  useAcademicsLibraryState,
  useAcademicsLibraryActions,
} from '../../contexts/academicsLibrary.context';
import dayjs, { Dayjs } from 'dayjs';
import { useAcademicsService } from 'src/apis/academics/useAcademicsService';
import {
  zNumberFromInput,
  zOptionalNullablePickerDate,
  zOptionalNumberFromInput,
  zOptionalYearFromInput,
} from 'src/utils/zodFormHelpers';

type LibraryItemFormData = {
  assetCode: string;
  title: string;
  assetType: LibraryAssetType;
  description?: string;
  condition: MaterialCondition;
  currentStatus: BorrowingStatus;
  location: string;
  purchaseDate?: Dayjs | null;
  purchaseCost?: number;
  supplier?: string;
  tags?: string;
  isbn?: string;
  author?: string;
  publisher?: string;
  edition?: string;
  publicationYear?: number;
  subject?: string;
  gradeLevel?: string;
  language?: string;
  maxBorrowDays: number;
  renewalAllowed: boolean;
  maxRenewals?: number;
  canBeBorrowed: boolean;
  isAvailable: boolean;
  notes?: string;
};

const getLibraryItemFormSchema = (
  translateText: (key: string) => string
): ZodType<LibraryItemFormData> =>
  z.object({
    assetCode: z.string().min(1, translateText('common.fieldRequired')),
    title: z.string().min(1, translateText('common.fieldRequired')),
    assetType: z.nativeEnum(LibraryAssetType, {
      required_error: translateText('common.fieldRequired'),
    }),
    description: z.string().optional(),
    condition: z.nativeEnum(MaterialCondition, {
      required_error: translateText('common.fieldRequired'),
    }),
    currentStatus: z.nativeEnum(BorrowingStatus, {
      required_error: translateText('common.fieldRequired'),
    }),
    location: z.string().min(1, translateText('common.fieldRequired')),
    purchaseDate: zOptionalNullablePickerDate({
      message: translateText('common.dateInvalid') || undefined,
    }),
    purchaseCost: zOptionalNumberFromInput({
      min: 0,
      message: translateText('common.fieldRequired'),
    }),
    supplier: z.string().optional(),
    tags: z.string().optional(),
    isbn: z.string().optional(),
    author: z.string().optional(),
    publisher: z.string().optional(),
    edition: z.string().optional(),
    publicationYear: zOptionalYearFromInput({
      message: translateText('finance.form.academicYearInvalid') || undefined,
    }),
    subject: z.string().optional(),
    gradeLevel: z.string().optional(),
    language: z.string().optional(),
    maxBorrowDays: zNumberFromInput({
      min: 1,
      message: translateText('common.fieldRequired'),
    }),
    renewalAllowed: z.boolean(),
    maxRenewals: zOptionalNumberFromInput({
      min: 0,
      message: translateText('common.fieldRequired'),
    }),
    canBeBorrowed: z.boolean(),
    isAvailable: z.boolean(),
    notes: z.string().optional(),
  }) as ZodType<LibraryItemFormData>;

const getAssetTypeOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: LibraryAssetType.BOOK,
      value: translateText('academics.library.assetTypes.book') || 'Book',
    },
    {
      key: LibraryAssetType.STUDY_MATERIAL,
      value:
        translateText('academics.library.assetTypes.studyMaterial') ||
        'Study Material',
    },
    {
      key: LibraryAssetType.LAB_EQUIPMENT,
      value:
        translateText('academics.library.assetTypes.labEquipment') ||
        'Lab Equipment',
    },
    {
      key: LibraryAssetType.DISK,
      value: translateText('academics.library.assetTypes.disk') || 'Disk',
    },
    {
      key: LibraryAssetType.AUDIO_VISUAL,
      value:
        translateText('academics.library.assetTypes.audioVisual') ||
        'Audio Visual',
    },
    {
      key: LibraryAssetType.DIGITAL_RESOURCE,
      value:
        translateText('academics.library.assetTypes.digitalResource') ||
        'Digital Resource',
    },
    {
      key: LibraryAssetType.MAGAZINE,
      value: translateText('academics.library.assetTypes.magazine') || 'Magazine',
    },
    {
      key: LibraryAssetType.NEWSPAPER,
      value:
        translateText('academics.library.assetTypes.newspaper') || 'Newspaper',
    },
    {
      key: LibraryAssetType.REFERENCE_MATERIAL,
      value:
        translateText('academics.library.assetTypes.referenceMaterial') ||
        'Reference Material',
    },
    {
      key: LibraryAssetType.OTHER,
      value: translateText('academics.library.assetTypes.other') || 'Other',
    },
  ];

const getStatusOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: BorrowingStatus.AVAILABLE,
      value: translateText('academics.library.statuses.available') || 'Available',
    },
    {
      key: BorrowingStatus.BORROWED,
      value: translateText('academics.library.statuses.borrowed') || 'Borrowed',
    },
    {
      key: BorrowingStatus.RESERVED,
      value: translateText('academics.library.statuses.reserved') || 'Reserved',
    },
    {
      key: BorrowingStatus.OVERDUE,
      value: translateText('academics.library.statuses.overdue') || 'Overdue',
    },
    {
      key: BorrowingStatus.LOST,
      value: translateText('academics.library.statuses.lost') || 'Lost',
    },
    {
      key: BorrowingStatus.DAMAGED,
      value: translateText('academics.library.statuses.damaged') || 'Damaged',
    },
    {
      key: BorrowingStatus.MAINTENANCE,
      value:
        translateText('academics.library.statuses.maintenance') || 'Maintenance',
    },
    {
      key: BorrowingStatus.RETIRED,
      value: translateText('academics.library.statuses.retired') || 'Retired',
    },
  ];

const getConditionOptions = (
  translateText: (key: string) => string
): TKeyValue<string>[] => [
    {
      key: MaterialCondition.NEW,
      value: translateText('academics.materials.conditions.new') || 'New',
    },
    {
      key: MaterialCondition.LIKE_NEW,
      value:
        translateText('academics.materials.conditions.likeNew') || 'Like New',
    },
    {
      key: MaterialCondition.GOOD,
      value: translateText('academics.materials.conditions.good') || 'Good',
    },
    {
      key: MaterialCondition.FAIR,
      value: translateText('academics.materials.conditions.fair') || 'Fair',
    },
    {
      key: MaterialCondition.POOR,
      value: translateText('academics.materials.conditions.poor') || 'Poor',
    },
  ];

const AddEditLibraryItemView: FC = () => {
  const { translateText } = useTranslation();
  const { createAcademicsLibraryItem, patchAcademicsLibraryItem } =
    useAcademicsService();
  const libraryItemFormSchema = getLibraryItemFormSchema(translateText);
  const queryClient = useQueryClient();

  const userFromStorage = localStorageGet('loggedInUser', undefined) as
    | ILoggedInUser
    | undefined;
  const loggedInUserId = userFromStorage?.id || '';

  const {
    academicsLibraryAppState: {
      isManageLibraryItemOpen,
      selectedLibraryItem,
      isCreateMode,
      libraryItemErrorMessage,
    },
  } = useAcademicsLibraryState();

  const { setIsManageLibraryItemOpen, setLibraryItemErrorMessage } =
    useAcademicsLibraryActions();

  // Create mutation
  const createMutation = useMutation({
    mutationFn: createAcademicsLibraryItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['get-academics-library'] });
      setIsManageLibraryItemOpen(false);
      setLibraryItemErrorMessage('');
    },
    onError: (err: any) => {
      setLibraryItemErrorMessage(err?.message || 'Failed to create library item');
    },
  });

  // Patch mutation
  const patchMutation = useMutation({
    mutationFn: patchAcademicsLibraryItem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['get-academics-library'] });
      setIsManageLibraryItemOpen(false);
      setLibraryItemErrorMessage('');
    },
    onError: (err: any) => {
      setLibraryItemErrorMessage(err?.message || 'Failed to update library item');
    },
  });

  const defaultValues: LibraryItemFormData = {
    assetCode: '',
    title: '',
    assetType: LibraryAssetType.BOOK,
    description: '',
    condition: MaterialCondition.NEW,
    currentStatus: BorrowingStatus.AVAILABLE,
    location: '',
    purchaseDate: null,
    purchaseCost: undefined,
    supplier: '',
    tags: '',
    isbn: '',
    author: '',
    publisher: '',
    edition: '',
    publicationYear: undefined,
    subject: '',
    gradeLevel: '',
    language: '',
    maxBorrowDays: 14,
    renewalAllowed: true,
    maxRenewals: 2,
    canBeBorrowed: true,
    isAvailable: true,
    notes: '',
  };

  const methods = useForm<LibraryItemFormData>({
    resolver: zodResolver(libraryItemFormSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = methods;

  const assetType = watch('assetType');

  useEffect(() => {
    if (selectedLibraryItem && isManageLibraryItemOpen) {
      reset({
        assetCode: selectedLibraryItem.assetCode || '',
        title: selectedLibraryItem.title || '',
        assetType: selectedLibraryItem.assetType || LibraryAssetType.BOOK,
        description: selectedLibraryItem.description || '',
        condition: selectedLibraryItem.condition || MaterialCondition.NEW,
        currentStatus:
          selectedLibraryItem.currentStatus || BorrowingStatus.AVAILABLE,
        location: selectedLibraryItem.location || '',
        purchaseDate: selectedLibraryItem.purchaseDate
          ? dayjs(selectedLibraryItem.purchaseDate)
          : null,
        purchaseCost: selectedLibraryItem.purchaseCost,
        supplier: selectedLibraryItem.supplier || '',
        tags: selectedLibraryItem.tags?.join(', ') || '',
        isbn: selectedLibraryItem.isbn || '',
        author: selectedLibraryItem.author || '',
        publisher: selectedLibraryItem.publisher || '',
        edition: selectedLibraryItem.edition || '',
        publicationYear: selectedLibraryItem.publicationYear,
        subject: selectedLibraryItem.subject || '',
        gradeLevel: selectedLibraryItem.gradeLevel || '',
        language: selectedLibraryItem.language || '',
        maxBorrowDays: selectedLibraryItem.maxBorrowDays || 14,
        renewalAllowed: selectedLibraryItem.renewalAllowed ?? true,
        maxRenewals: selectedLibraryItem.maxRenewals || 2,
        canBeBorrowed: selectedLibraryItem.canBeBorrowed ?? true,
        isAvailable: selectedLibraryItem.isAvailable ?? true,
        notes: selectedLibraryItem.notes || '',
      });
    } else if (isManageLibraryItemOpen) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLibraryItem, isManageLibraryItemOpen, reset]);

  const onSubmitForm = (data: LibraryItemFormData) => {
    const timestamp = new Date().toISOString();

    const libraryItemData: IAcademicsLibraryItem = {
      id: selectedLibraryItem?.id || '',
      assetCode: data.assetCode,
      title: data.title,
      assetType: data.assetType,
      description: data.description || undefined,
      condition: data.condition,
      currentStatus: data.currentStatus,
      location: data.location,
      purchaseDate: data.purchaseDate
        ? data.purchaseDate.format('YYYY-MM-DD')
        : undefined,
      purchaseCost: data.purchaseCost,
      supplier: data.supplier || undefined,
      tags: data.tags ? data.tags.split(',').map((t) => t.trim()) : [],
      isbn: data.isbn || undefined,
      author: data.author || undefined,
      publisher: data.publisher || undefined,
      edition: data.edition || undefined,
      publicationYear: data.publicationYear,
      subject: data.subject || undefined,
      gradeLevel: data.gradeLevel || undefined,
      language: data.language || undefined,
      maxBorrowDays: data.maxBorrowDays,
      renewalAllowed: data.renewalAllowed,
      maxRenewals: data.maxRenewals || 0,
      canBeBorrowed: data.canBeBorrowed,
      isAvailable: data.isAvailable,
      notes: data.notes || undefined,
      borrowingHistory: selectedLibraryItem?.borrowingHistory || [],
      damageHistory: selectedLibraryItem?.damageHistory || [],
      totalBorrows: selectedLibraryItem?.totalBorrows || 0,
      totalDamages: selectedLibraryItem?.totalDamages || 0,
      createdAt: selectedLibraryItem?.createdAt || timestamp,
      createdBy: selectedLibraryItem?.createdBy || loggedInUserId,
      updatedAt: timestamp,
      updatedBy: loggedInUserId,
      currentBorrowing: selectedLibraryItem?.currentBorrowing,
    };

    if (isCreateMode) {
      createMutation.mutate(libraryItemData);
    } else {
      patchMutation.mutate(libraryItemData);
    }
  };

  const handleClose = () => {
    setIsManageLibraryItemOpen(false);
    setLibraryItemErrorMessage('');
    reset(defaultValues);
  };

  return (
    <div data-testid="add-edit-library-item-container">
      <StructuredModal
        isOpen={isManageLibraryItemOpen}
        onClose={handleClose}
        title={
          isCreateMode
            ? translateText('academics.library.addLibraryItem') ||
            'Add Library Item'
            : translateText('academics.library.editLibraryItem') ||
            'Edit Library Item'
        }
        drawerWidth="80vw"
        footerPositiveAction={handleSubmit(onSubmitForm)}
        footerPositiveActionText={
          isCreateMode
            ? translateText('common.save')
            : translateText('common.update')
        }
        isLoading={createMutation.isPending || patchMutation.isPending}
        footerNegativeAction={handleClose}
        footerNegativeActionText={translateText('common.cancel')}
        footerMessage={
          libraryItemErrorMessage ? (
            <Typography
              color="error"
              variant="body2"
              data-testid="library-item-error-message"
            >
              {libraryItemErrorMessage}
            </Typography>
          ) : null
        }
      >
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(onSubmitForm)}
            style={{ width: '100%' }}
            data-testid="library-item-form"
          >
            <Grid
              container
              spacing={{ xs: 2, sm: 3 }}
              padding={{ xs: 1, sm: 2 }}
              data-testid="library-item-form-container"
            >
              <Grid item xs={12} data-testid="library-item-basic-section">
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
                >
                  {translateText('academics.library.basicDetails') ||
                    'Basic Details'}
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                data-testid="library-item-asset-code-field"
              >
                <ControlledTextField
                  control={control}
                  name="assetCode"
                  id="library-item-asset-code"
                  label={
                    translateText('academics.library.assetCode') || 'Asset Code'
                  }
                  errorMessage={errors.assetCode?.message}
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={6}
                md={6}
                data-testid="library-item-title-field"
              >
                <ControlledTextField
                  control={control}
                  name="title"
                  id="library-item-title"
                  label={translateText('common.title') || 'Title'}
                  errorMessage={errors.title?.message}
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="library-item-asset-type-field"
              >
                <ControlledSelectField
                  control={control}
                  name="assetType"
                  id="library-item-asset-type"
                  label={
                    translateText('academics.library.assetType') || 'Asset Type'
                  }
                  itemList={getAssetTypeOptions(translateText)}
                  errorMessage={errors.assetType?.message}
                  isRequired
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} md={6} data-testid="library-item-status-field">
                <ControlledSelectField
                  control={control}
                  name="currentStatus"
                  id="library-item-status"
                  label={translateText('academics.library.status') || 'Status'}
                  itemList={getStatusOptions(translateText)}
                  errorMessage={errors.currentStatus?.message}
                  isRequired
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="library-item-condition-field"
              >
                <ControlledSelectField
                  control={control}
                  name="condition"
                  id="library-item-condition"
                  label={
                    translateText('academics.library.condition') || 'Condition'
                  }
                  itemList={getConditionOptions(translateText)}
                  errorMessage={errors.condition?.message}
                  isRequired
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="library-item-location-field"
              >
                <ControlledTextField
                  control={control}
                  name="location"
                  id="library-item-location"
                  label={
                    translateText('academics.library.location') || 'Location'
                  }
                  errorMessage={errors.location?.message}
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} data-testid="library-item-description-field">
                <ControlledTextField
                  control={control}
                  name="description"
                  id="library-item-description"
                  label={translateText('common.description') || 'Description'}
                  errorMessage={errors.description?.message}
                  isGridItem={false}
                  multiline
                  rows={3}
                  fullWidth
                />
              </Grid>

              {(assetType === LibraryAssetType.BOOK ||
                assetType === LibraryAssetType.STUDY_MATERIAL) && (
                  <>
                    <Grid item xs={12} data-testid="library-item-book-section">
                      <Typography variant="h6" gutterBottom>
                        {translateText('academics.library.bookDetails') ||
                          'Book Details'}
                      </Typography>
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      md={6}
                      data-testid="library-item-isbn-field"
                    >
                      <ControlledTextField
                        control={control}
                        name="isbn"
                        id="library-item-isbn"
                        label={translateText('academics.library.isbn') || 'ISBN'}
                        errorMessage={errors.isbn?.message}
                        isGridItem={false}
                      />
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      md={6}
                      data-testid="library-item-author-field"
                    >
                      <ControlledTextField
                        control={control}
                        name="author"
                        id="library-item-author"
                        label={
                          translateText('academics.library.author') || 'Author'
                        }
                        errorMessage={errors.author?.message}
                        isGridItem={false}
                      />
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      md={6}
                      data-testid="library-item-publisher-field"
                    >
                      <ControlledTextField
                        control={control}
                        name="publisher"
                        id="library-item-publisher"
                        label={
                          translateText('academics.library.publisher') ||
                          'Publisher'
                        }
                        errorMessage={errors.publisher?.message}
                        isGridItem={false}
                      />
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      md={6}
                      data-testid="library-item-edition-field"
                    >
                      <ControlledTextField
                        control={control}
                        name="edition"
                        id="library-item-edition"
                        label={
                          translateText('academics.library.edition') || 'Edition'
                        }
                        errorMessage={errors.edition?.message}
                        isGridItem={false}
                      />
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      md={6}
                      data-testid="library-item-publication-year-field"
                    >
                      <ControlledTextField
                        control={control}
                        name="publicationYear"
                        id="library-item-publication-year"
                        label={
                          translateText('academics.library.publicationYear') ||
                          'Publication Year'
                        }
                        errorMessage={errors.publicationYear?.message}
                        type="number"
                        isGridItem={false}
                      />
                    </Grid>
                  </>
                )}

              <Grid item xs={12} data-testid="library-item-purchase-section">
                <Typography variant="h6" gutterBottom>
                  {translateText('academics.library.purchaseDetails') ||
                    'Purchase Details'}
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="library-item-purchase-date-field"
              >
                <ControlledDateField
                  control={control}
                  name="purchaseDate"
                  id="library-item-purchase-date"
                  label={
                    translateText('academics.materials.purchaseDate') ||
                    'Purchase Date'
                  }
                  errorMessage={errors.purchaseDate?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="library-item-purchase-cost-field"
              >
                <ControlledTextField
                  control={control}
                  name="purchaseCost"
                  id="library-item-purchase-cost"
                  label={
                    translateText('academics.library.purchaseCost') ||
                    'Purchase Cost'
                  }
                  errorMessage={errors.purchaseCost?.message}
                  type="number"
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="library-item-supplier-field"
              >
                <ControlledTextField
                  control={control}
                  name="supplier"
                  id="library-item-supplier"
                  label={
                    translateText('academics.library.supplier') || 'Supplier'
                  }
                  errorMessage={errors.supplier?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} data-testid="library-item-borrowing-section">
                <Typography variant="h6" gutterBottom>
                  {translateText('academics.library.borrowingSettings') ||
                    'Borrowing Settings'}
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="library-item-max-borrow-days-field"
              >
                <ControlledTextField
                  control={control}
                  name="maxBorrowDays"
                  id="library-item-max-borrow-days"
                  label={
                    translateText('academics.library.maxBorrowDays') ||
                    'Max Borrow Days'
                  }
                  errorMessage={errors.maxBorrowDays?.message}
                  type="number"
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="library-item-max-renewals-field"
              >
                <ControlledTextField
                  control={control}
                  name="maxRenewals"
                  id="library-item-max-renewals"
                  label={
                    translateText('academics.library.maxRenewals') ||
                    'Max Renewals'
                  }
                  errorMessage={errors.maxRenewals?.message}
                  type="number"
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} data-testid="library-item-additional-section">
                <Typography variant="h6" gutterBottom>
                  {translateText('academics.library.additionalDetails') ||
                    'Additional Details'}
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="library-item-subject-field"
              >
                <ControlledTextField
                  control={control}
                  name="subject"
                  id="library-item-subject"
                  label={
                    translateText('academics.library.subject') || 'Subject'
                  }
                  errorMessage={errors.subject?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="library-item-grade-level-field"
              >
                <ControlledTextField
                  control={control}
                  name="gradeLevel"
                  id="library-item-grade-level"
                  label={
                    translateText('academics.library.gradeLevel') ||
                    'Grade Level'
                  }
                  errorMessage={errors.gradeLevel?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="library-item-language-field"
              >
                <ControlledTextField
                  control={control}
                  name="language"
                  id="library-item-language"
                  label={
                    translateText('academics.library.language') || 'Language'
                  }
                  errorMessage={errors.language?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} md={6} data-testid="library-item-tags-field">
                <ControlledTextField
                  control={control}
                  name="tags"
                  id="library-item-tags"
                  label={translateText('academics.library.tags') || 'Tags'}
                  helperText={
                    translateText('academics.library.tagsHelper') ||
                    'Separate tags with commas'
                  }
                  errorMessage={errors.tags?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} data-testid="library-item-notes-field">
                <ControlledTextField
                  control={control}
                  name="notes"
                  id="library-item-notes"
                  label={translateText('common.remarks') || 'Notes'}
                  errorMessage={errors.notes?.message}
                  isGridItem={false}
                  multiline
                  rows={3}
                  fullWidth
                />
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </StructuredModal>
    </div>
  );
};

export default AddEditLibraryItemView;
